// propose_clarify envelopes: the single-question picker and the structured
// form variant.

import type {
  BlockOutput,
  ClarifyBlockData,
  ClarifyFieldSearch,
  ClarifyOption,
  ClarifyOptionJob,
} from '../../types';
import { arr, num, str } from './guards';
import type { StructuredClarify, StructuredClarifyForm } from './types';

// Structured per-job metadata the backend may attach to a picker option, so
// the clarify job dropdown can render title + meta + status tags instead of a
// flat label (CORE-4957). Absent → the option keeps its plain label.
const asJobMeta = (raw: unknown): ClarifyOptionJob | undefined => {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const j = raw as Record<string, unknown>;
  const meta: ClarifyOptionJob = {
    title: str(j.title),
    status: str(j.status),
    statusLabel: str(j.status_label),
    confidential: j.confidential === true ? true : undefined,
    department: str(j.department),
    office: str(j.office),
    workType: str(j.work_type),
    recruiter: str(j.recruiter),
    hiringManager: str(j.hiring_manager),
  };
  return Object.values(meta).some((v) => v !== undefined) ? meta : undefined;
};

const asOptions = (raw: unknown): ClarifyOption[] =>
  arr(raw)
    .map((o) => {
      if (typeof o !== 'object' || o === null) return null;
      const obj = o as { value?: unknown; label?: unknown; job?: unknown };
      const value = str(obj.value) ?? '';
      const label = str(obj.label) ?? '';
      if (!value || !label) return null;
      return { value, label, job: asJobMeta(obj.job) };
    })
    .filter((o): o is ClarifyOption => o !== null);

// On-the-fly search descriptor (unbounded pickers: users, projects).
const asFieldSearch = (raw: unknown): ClarifyFieldSearch | undefined => {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const s = raw as {
    tool?: unknown;
    input?: unknown;
    label_column?: unknown;
    value_prefix?: unknown;
  };
  const tool = str(s.tool);
  if (!tool) return undefined;
  return {
    tool,
    input:
      typeof s.input === 'object' && s.input !== null
        ? (s.input as Record<string, unknown>)
        : undefined,
    labelColumn: str(s.label_column),
    valuePrefix: str(s.value_prefix),
  };
};

export const buildClarifyBlock = (
  t: StructuredClarify,
  toolCallId?: string
): BlockOutput | null => {
  const intro = str(t.intro) ?? '';
  const pending = str(t.pending) ?? '';
  if (!intro || !pending) return null;
  const options = asOptions(t.options);
  if (options.length === 0) return null;
  const data: ClarifyBlockData = {
    intro,
    pending,
    options,
    submitLabel: str(t.submit_label),
    toolCallId,
  };
  return { type: 'clarify', data };
};

export const buildClarifyFormBlock = (
  t: StructuredClarifyForm,
  toolCallId?: string
): BlockOutput | null => {
  const intro = str(t.intro) ?? '';
  // The `{key}` template: backend calls it `pending`; tolerate the old `warning`.
  const template = str(t.pending) ?? str(t.warning) ?? '';
  if (!intro || !template) return null;
  // Backend emits `questions`; tolerate the old `actions`.
  const rawQuestions = Array.isArray(t.questions)
    ? t.questions
    : Array.isArray(t.actions)
      ? t.actions
      : [];
  const actions = rawQuestions
    .map((a) => {
      if (typeof a !== 'object' || a === null) return null;
      const obj = a as Record<string, unknown>;
      const key = str(obj.key) ?? '';
      const label = str(obj.label) ?? '';
      if (!key || !label) return null;
      const options = asOptions(obj.options);
      return {
        key,
        label,
        help: str(obj.help),
        prefill: str(obj.prefill) ?? '',
        placeholder: str(obj.placeholder),
        required: obj.required === true,
        priority: num(obj.priority) ?? 0,
        type: str(obj.type) ?? (options.length > 0 ? 'picker' : 'text'),
        options,
        selected: str(obj.selected) ?? '',
        multi: obj.multi === true,
        search: asFieldSearch(obj.search),
      };
    })
    .filter((a): a is NonNullable<typeof a> => a !== null)
    .sort((a, b) => a.priority - b.priority);
  if (actions.length === 0) return null;
  return {
    type: 'clarifyForm',
    data: {
      intro,
      warning: template,
      actions,
      submitLabel: str(t.submit_label) || str(t.it_label) || 'Continue',
      toolCallId,
    },
  };
};
